import { useState, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export function useBreathingBackground() {
  const [customVideos, setCustomVideos] = useState([]);
  const [selectedBg, setSelectedBg] = useState(0);
  const [hoveredVideo, setHoveredVideo] = useState(null);
  const [backgroundImages, setBackgroundImages] = useState([]);
  const [selectedImageBg, setSelectedImageBg] = useState(null);
  const [selectedAnimation, setSelectedAnimation] = useState('breathe');
  const [uploadingVideo, setUploadingVideo] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [defaultVideoId, setDefaultVideoId] = useState(null);
  const [defaultImageId, setDefaultImageId] = useState(null);

  const allBackgrounds = customVideos;
  const currentBg = allBackgrounds[selectedBg] || allBackgrounds[0];
  const isImageMode = selectedImageBg !== null;
  const currentImage = isImageMode ? backgroundImages[selectedImageBg] : null;

  const fetchBackgroundData = useCallback(async () => {
    try {
      const [videosRes, imagesRes, defaultVideoRes, defaultImageRes] = await Promise.all([
        axios.get(`${API}/api/breathing-videos`),
        axios.get(`${API}/api/breathing-images`),
        axios.get(`${API}/api/breathing-config/default-video`),
        axios.get(`${API}/api/breathing-config/default-image`)
      ]);
      setCustomVideos(videosRes.data);
      setBackgroundImages(imagesRes.data);

      const defImageId = defaultImageRes.data?.image_id;
      const defVideoId = defaultVideoRes.data?.video_id;

      // Image par défaut prioritaire sur vidéo par défaut
      if (defImageId) {
        setDefaultImageId(defImageId);
        const defaultImgIndex = imagesRes.data.findIndex(img => img.id === defImageId);
        if (defaultImgIndex >= 0) setSelectedImageBg(defaultImgIndex);
      } else if (defVideoId) {
        setDefaultVideoId(defVideoId);
        const defaultIndex = videosRes.data.findIndex(v => v.id === defVideoId);
        if (defaultIndex >= 0) setSelectedBg(defaultIndex);
      }
    } catch (error) {
      console.error('Error fetching backgrounds:', error);
    }
  }, []);

  const handleVideoUpload = async (file) => {
    if (!file) return;
    setUploadingVideo(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('name', file.name.replace(/\.[^/.]+$/, ''));
    try {
      const response = await axios.post(`${API}/api/breathing-videos/upload`, formData);
      setCustomVideos(prev => [...prev, response.data]);
    } catch (error) {
      console.error('Error uploading video:', error);
      alert('Erreur lors de l\'upload de la vidéo');
    } finally { setUploadingVideo(false); }
  };

  const handleVideoRename = async (videoId, newName) => {
    try {
      const formData = new FormData();
      formData.append('name', newName);
      await axios.put(`${API}/api/breathing-videos/${videoId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCustomVideos(prev => prev.map(v => v.id === videoId ? { ...v, name: newName } : v));
    } catch (e) { console.error('Error renaming video:', e); }
  };

  const handleVideoToggleLock = async (video) => {
    try {
      const formData = new FormData();
      formData.append('locked', (!video.locked).toString());
      await axios.put(`${API}/api/breathing-videos/${video.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCustomVideos(prev => prev.map(v => v.id === video.id ? { ...v, locked: !v.locked } : v));
    } catch (e) { console.error('Error toggling lock:', e); }
  };

  const handleVideoDelete = async (video) => {
    if (video.locked) return;
    try {
      await axios.delete(`${API}/api/breathing-videos/${video.id}`);
      setCustomVideos(prev => prev.filter(v => v.id !== video.id));
      if (selectedBg >= customVideos.length - 1) setSelectedBg(0);
    } catch (e) { console.error('Error deleting video:', e); }
  };

  const handleSetDefaultVideo = async (videoId) => {
    try {
      const formData = new FormData();
      formData.append('video_id', videoId);
      await axios.put(`${API}/api/breathing-config/default-video`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDefaultVideoId(videoId);
      // Quand on met une vidéo par défaut, on retire l'image par défaut
      setDefaultImageId(null);
      try { await axios.delete(`${API}/api/breathing-config/default-image`); } catch (_) {}
    } catch (e) { console.error('Error setting default video:', e); }
  };

  const handleSetDefaultImage = async (imageId) => {
    try {
      const formData = new FormData();
      formData.append('image_id', imageId);
      await axios.put(`${API}/api/breathing-config/default-image`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setDefaultImageId(imageId);
      setDefaultVideoId(null);
      try { await axios.delete(`${API}/api/breathing-config/default-video`); } catch (_) {}
    } catch (e) { console.error('Error setting default image:', e); }
  };

  const handleImageRename = async (imageId, newName) => {
    try {
      const formData = new FormData();
      formData.append('name', newName);
      await axios.put(`${API}/api/breathing-images/${imageId}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBackgroundImages(prev => prev.map(img => img.id === imageId ? { ...img, name: newName } : img));
    } catch (e) { console.error('Error renaming image:', e); }
  };

  const handleImageToggleLock = async (image) => {
    try {
      const formData = new FormData();
      formData.append('locked', (!image.locked).toString());
      await axios.put(`${API}/api/breathing-images/${image.id}`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBackgroundImages(prev => prev.map(img => img.id === image.id ? { ...img, locked: !img.locked } : img));
    } catch (e) { console.error('Error toggling lock:', e); }
  };

  const handleImageDelete = async (image) => {
    if (image.locked) return;
    try {
      await axios.delete(`${API}/api/breathing-images/${image.id}`);
      setBackgroundImages(prev => prev.filter(img => img.id !== image.id));
      if (selectedImageBg !== null && selectedImageBg >= backgroundImages.length - 1) {
        setSelectedImageBg(backgroundImages.length > 1 ? 0 : null);
      }
    } catch (e) { console.error('Error deleting image:', e); }
  };

  const getAnimationClass = (animation) => {
    const animations = {
      'breathe': 'animate-breathe', 'zoom-slow': 'animate-zoom-slow',
      'pan-right': 'animate-pan-right', 'pan-left': 'animate-pan-left',
      'pulse-glow': 'animate-pulse-glow', 'float': 'animate-float'
    };
    return animations[animation] || 'animate-breathe';
  };

  return {
    customVideos, selectedBg, setSelectedBg, hoveredVideo, setHoveredVideo,
    backgroundImages, selectedImageBg, setSelectedImageBg, selectedAnimation, setSelectedAnimation,
    uploadingVideo, uploadingImage, setUploadingImage, defaultVideoId, defaultImageId,
    allBackgrounds, currentBg, isImageMode, currentImage,
    fetchBackgroundData, handleVideoUpload, handleVideoRename,
    handleVideoToggleLock, handleVideoDelete, handleSetDefaultVideo, handleSetDefaultImage,
    handleImageRename, handleImageToggleLock, handleImageDelete, getAnimationClass
  };
}
